import React, { useEffect, useState } from 'react';
import apiClient from '../../apiClient';
import { FaUserPlus, FaUserMinus, FaSearch, FaTimes, FaUserTag } from 'react-icons/fa';
import './AssignStakeholdersModal.css';

function groupByRole(items, isEmployee = false) {
  const grouped = {};
  items.forEach(item => {
    // Use different field names based on whether it's employee or stakeholder data
    const role = isEmployee ? (item.job_position || 'Unspecified') : (item.job_title || 'Unspecified');
    if (!grouped[role]) grouped[role] = [];
    grouped[role].push(item);
  });
  return grouped;
}

const AssignStakeholdersModal = ({ isOpen, onClose, assessmentId, projectId, onAssignmentChange }) => {
  const [allStakeholders, setAllStakeholders] = useState([]);
  const [assignedStakeholders, setAssignedStakeholders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !assessmentId || !projectId) return;
    
    setLoading(true);
    setError(null);
    // Make API calls individually with better error handling
    const loadProjectStakeholders = apiClient.get(`/projects/${projectId}/stakeholders`)
      .then(res => {
        const projectStakeholders = res.data.stakeholders || [];
        console.log('✅ Project stakeholders loaded:', projectStakeholders);
        return projectStakeholders;
      })
      .catch(e => {
        console.error('❌ Failed to load project stakeholders:', e);
        setError('Failed to load project stakeholders. Please ensure stakeholders are assigned to this project.');
        throw e;
      });

    // Load employees to cross-reference with stakeholders
    const loadEmployees = apiClient.get('/employees')
      .then(res => {
        const employees = res.data.employees || res.data || [];
        console.log('✅ Employees loaded for cross-reference:', employees.length);
        return employees;
      })
      .catch(e => {
        console.error('❌ Failed to load employees:', e);
        return []; // Don't fail the whole process, just continue without employee cross-reference
      });

    const loadAssignedEmployees = apiClient.get(`/assessments/${assessmentId}/stakeholders`)
      .then(res => {
        const assignedEmployees = res.data || [];
        console.log('✅ Assigned employees loaded:', assignedEmployees);
        return assignedEmployees;
      })
      .catch(e => {
        console.error('❌ Failed to load assigned employees:', e);
        // Don't throw - this is optional, return empty array
        console.log('ℹ️ No assigned employees found, continuing...');
        return [];
      });

    const loadExternalAssignments = apiClient.get(`/guest/assignments/${assessmentId}`)
      .then(res => {
        // Handle various response formats
        let rawAssignments = [];
        if (Array.isArray(res.data)) {
          rawAssignments = res.data;
        } else if (res.data && Array.isArray(res.data.assignments)) {
          rawAssignments = res.data.assignments;
        } else if (res.data && Array.isArray(res.data.invitations)) {
          // Handle API returning 'invitations' key (even though they're assignments)
          rawAssignments = res.data.invitations;
        } else if (res.data && typeof res.data === 'object') {
          // If it's an object but not an array, check if it has data we can use
          console.warn('⚠️ Unexpected response format for external assignments:', res.data);
          rawAssignments = [];
        }
        
        console.log('✅ Raw external assignments loaded:', rawAssignments);
        console.log('  Type:', Array.isArray(rawAssignments) ? 'Array' : typeof rawAssignments);
        console.log('  Length:', rawAssignments.length || 0);
        
        // Ensure we have an array before calling filter
        if (!Array.isArray(rawAssignments)) {
          console.error('❌ External assignments is not an array:', rawAssignments);
          return [];
        }
        
        // Deduplicate assignments by email to prevent duplicate entries
        const seen = new Set();
        const deduplicatedAssignments = rawAssignments.filter(assignment => {
          const email = assignment.recipient_email || assignment.email;
          if (seen.has(email)) {
            console.log(`⚠️ Duplicate assignment found for ${email}, skipping...`);
            return false;
          }
          seen.add(email);
          return true;
        });
        
        console.log('✅ Deduplicated external assignments:', deduplicatedAssignments);
        return deduplicatedAssignments;
      })
      .catch(e => {
        console.error('❌ Failed to load external assignments:', e);
        // Don't throw - this is optional, return empty array
        console.log('ℹ️ No external assignments found, continuing...');
        return [];
      });

    Promise.all([loadProjectStakeholders, loadEmployees, loadAssignedEmployees, loadExternalAssignments])
      .then(([projectStakeholders, employees, assignedEmployees, externalAssignments]) => {
        console.log('📋 All data loaded successfully:');
        console.log('  - Project stakeholders:', projectStakeholders.length);
        console.log('  - Employees:', employees.length);
        console.log('  - Assigned employees:', assignedEmployees.length);
        console.log('  - External assignments:', externalAssignments.length);
        
        // Create email-to-employee lookup for cross-referencing
        const employeesByEmail = {};
        employees.forEach(emp => {
          if (emp.email) {
            employeesByEmail[emp.email.toLowerCase()] = emp;
          }
        });
        
        // Enhance stakeholders with employee information
        const enhancedStakeholders = projectStakeholders.map(stakeholder => {
          const stakeholderEmail = stakeholder.email?.toLowerCase();
          const matchingEmployee = stakeholderEmail ? employeesByEmail[stakeholderEmail] : null;
          
          if (matchingEmployee && !stakeholder.employee_id) {
            console.log(`🔗 Auto-linking stakeholder ${stakeholder.first_name} ${stakeholder.last_name} to employee ${matchingEmployee.name}`);
            return {
              ...stakeholder,
              employee_id: matchingEmployee.id,
              stakeholder_category: 'internal',
              stakeholder_type: 'employee'
            };
          }
          
          return stakeholder;
        });
        
        setAllStakeholders(enhancedStakeholders);
        
        // Combine internal assignments and external assignments for display
        const combinedAssigned = [
          ...assignedEmployees,
          ...externalAssignments.map(assignment => ({
            id: `ext_${assignment.id}`,
            name: assignment.participant_name || assignment.recipient_name || 'Unknown',
            email: assignment.email || assignment.recipient_email || '',
            job_position: 'External Stakeholder',
            isExternal: true,
            assignmentId: assignment.id
          }))
        ];
        
        setAssignedStakeholders(combinedAssigned);
        
        // Clear any previous errors if we got here successfully
        setError(null);
      })
      .catch(e => {
        console.error('💥 Critical error in stakeholder loading:', e);
        // Error will only reach here if the critical project stakeholders call failed
        // (since the other calls have their own catch handlers that don't throw)
        if (e.response?.status === 404) {
          setError('Project not found or no stakeholders assigned to this project.');
        } else {
          setError('Failed to load project stakeholders. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [isOpen, assessmentId, projectId]);

  // Map assigned employee IDs and external stakeholder emails to filter out already assigned stakeholders
  const assignedEmployeeIds = new Set(
    assignedStakeholders
      .filter(emp => !emp.isExternal)
      .map(emp => emp.id)
  );
  const assignedExternalEmails = new Set(
    assignedStakeholders
      .filter(emp => emp.isExternal)
      .map(emp => emp.email)
  );
  
  // Show ALL stakeholders from the project - both internal and external
  // Filter out those already assigned
  const availableStakeholders = allStakeholders.filter(s => {
    // For internal stakeholders (those with employee_id), check if already assigned
    if (s.employee_id) {
      return !assignedEmployeeIds.has(s.employee_id);
    }
    // For external stakeholders (no employee_id), check if already invited
    return !assignedExternalEmails.has(s.email);
  });

  const filteredAvailable = availableStakeholders.filter(s => {
    const term = searchTerm.toLowerCase();
    const name = s.display_name || s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim();
    return (
      name.toLowerCase().includes(term) ||
      (s.email && s.email.toLowerCase().includes(term)) ||
      (s.job_title && s.job_title.toLowerCase().includes(term))
    );
  });
  const filteredAssigned = assignedStakeholders.filter(emp => {
    const term = searchTerm.toLowerCase();
    // Assigned stakeholders are employees, so use employee fields
    const name = emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
    return (
      name.toLowerCase().includes(term) ||
      (emp.email && emp.email.toLowerCase().includes(term)) ||
      (emp.job_position && emp.job_position.toLowerCase().includes(term))
    );
  });

  const handleAssign = async (stakeholder) => {
    setSaving(true);
    setError(null);
    console.log('Assigning stakeholder:', stakeholder);
    console.log('Stakeholder type:', stakeholder.employee_id ? 'Internal (Employee)' : 'External (Guest)');
    
    try {
      if (stakeholder.employee_id) {
        // INTERNAL STAKEHOLDER: Assign directly to assessment
        console.log('Assigning internal stakeholder with employee_id:', stakeholder.employee_id);
        const assignResponse = await apiClient.post(`/assessments/${assessmentId}/stakeholders`, { 
          employee_ids: [stakeholder.employee_id] 
        });
        console.log('Assignment response:', assignResponse.data);
        
        // Refresh the assigned stakeholders list
        const res = await apiClient.get(`/assessments/${assessmentId}/stakeholders`);
        console.log('Updated assigned stakeholders:', res.data);
        setAssignedStakeholders(res.data || []);
        
        if (onAssignmentChange) onAssignmentChange();
        
        // Show success message
        setError(`✅ Successfully assigned ${stakeholder.display_name || stakeholder.full_name} (they can access via My Assessments portal)`);
        setTimeout(() => setError(null), 5000);
        
      } else {
        // EXTERNAL STAKEHOLDER: Create assessment assignment (but don't send email yet)
        console.log('Creating assessment assignment for external stakeholder - no email sent yet:', stakeholder.email);
        const assignResponse = await apiClient.post(`/guest/assign-external-stakeholder`, {
          assessment_id: assessmentId,
          name: stakeholder.display_name || stakeholder.full_name || `${stakeholder.first_name} ${stakeholder.last_name}`,
          email: stakeholder.email,
          send_immediately: false  // Don't send email now, wait for deployment
        });
        console.log('Assignment created:', assignResponse.data);
        
        // Refresh the stakeholder lists to show the new invitation
        const [assignedRes, assignmentsRes] = await Promise.all([
          apiClient.get(`/assessments/${assessmentId}/stakeholders`),
          apiClient.get(`/guest/assignments/${assessmentId}`)
        ]);
        
        const assignedEmployees = assignedRes.data || [];
        
        // Handle various response formats for external assignments
        let rawAssignments = [];
        if (Array.isArray(assignmentsRes.data)) {
          rawAssignments = assignmentsRes.data;
        } else if (assignmentsRes.data && Array.isArray(assignmentsRes.data.assignments)) {
          rawAssignments = assignmentsRes.data.assignments;
        } else if (assignmentsRes.data && Array.isArray(assignmentsRes.data.invitations)) {
          // Handle API returning 'invitations' key (even though they're assignments)
          rawAssignments = assignmentsRes.data.invitations;
        }
        
        // Ensure we have an array before calling filter
        if (!Array.isArray(rawAssignments)) {
          console.error('❌ External assignments is not an array:', rawAssignments);
          rawAssignments = [];
        }
        
        // Deduplicate assignments by email
        const seen = new Set();
        const deduplicatedAssignments = rawAssignments.filter(assignment => {
          const email = assignment.recipient_email || assignment.email;
          if (seen.has(email)) return false;
          seen.add(email);
          return true;
        });
        
        const combinedAssigned = [
          ...assignedEmployees,
          ...deduplicatedAssignments.map(assignment => ({
            id: `ext_${assignment.id}`,
            name: assignment.participant_name || assignment.recipient_name || 'Unknown',
            email: assignment.email || assignment.recipient_email || '',
            job_position: 'External Stakeholder',
            isExternal: true,
            assignmentId: assignment.id
          }))
        ];
        
        setAssignedStakeholders(combinedAssigned);
        
        if (onAssignmentChange) onAssignmentChange();
        
        // Show success message
        setError(`✅ Successfully assigned ${stakeholder.display_name || stakeholder.full_name} (they will receive an email when the assessment is deployed)`);
        setTimeout(() => setError(null), 5000);
      }
      
    } catch (e) {
      console.error('Assignment error:', e);
      console.error('Error response:', e.response?.data);
      const stakeholderName = stakeholder.display_name || stakeholder.full_name;
      const errorMsg = e.response?.data?.error || `Failed to assign ${stakeholderName}`;
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (stakeholder) => {
    setSaving(true);
    setError(null);
    
    console.log('🗑️ Removing stakeholder:', stakeholder);
    console.log('  - Is external:', stakeholder.isExternal);
    console.log('  - Stakeholder ID:', stakeholder.id);
    console.log('  - Assignment ID:', stakeholder.assignmentId);
    
    try {
      if (stakeholder.isExternal) {
        // Remove external stakeholder assignment
        console.log(`🗑️ Deleting external assignment: /guest/assignments/${stakeholder.assignmentId}`);
        const deleteResponse = await apiClient.delete(`/guest/assignments/${stakeholder.assignmentId}`);
        console.log('✅ Delete assignment response:', deleteResponse.data);
      } else {
        // Remove internal stakeholder assignment
        console.log(`🗑️ Removing internal stakeholder: /assessments/${assessmentId}/stakeholders/${stakeholder.id}`);
        const deleteResponse = await apiClient.delete(`/assessments/${assessmentId}/stakeholders/${stakeholder.id}`);
        console.log('✅ Delete stakeholder response:', deleteResponse.data);
      }
      
      // Refresh both lists
      const [assignedRes, assignmentsRes] = await Promise.all([
        apiClient.get(`/assessments/${assessmentId}/stakeholders`),
        apiClient.get(`/guest/assignments/${assessmentId}`)
      ]);
      
      const assignedEmployees = assignedRes.data || [];
      
      // Handle various response formats for external assignments
      let rawAssignments = [];
      if (Array.isArray(assignmentsRes.data)) {
        rawAssignments = assignmentsRes.data;
      } else if (assignmentsRes.data && Array.isArray(assignmentsRes.data.assignments)) {
        rawAssignments = assignmentsRes.data.assignments;
      } else if (assignmentsRes.data && Array.isArray(assignmentsRes.data.invitations)) {
        // Handle API returning 'invitations' key (even though they're assignments)
        rawAssignments = assignmentsRes.data.invitations;
      }
      
      // Ensure we have an array before calling filter
      if (!Array.isArray(rawAssignments)) {
        console.error('❌ External assignments is not an array:', rawAssignments);
        rawAssignments = [];
      }
      
      // Deduplicate assignments by email
      const seen = new Set();
      const deduplicatedAssignments = rawAssignments.filter(assignment => {
        const email = assignment.recipient_email || assignment.email;
        if (seen.has(email)) return false;
        seen.add(email);
        return true;
      });
      
      const combinedAssigned = [
        ...assignedEmployees,
        ...deduplicatedAssignments.map(assignment => ({
          id: `ext_${assignment.id}`,
          name: assignment.participant_name || assignment.recipient_name || 'Unknown',
          email: assignment.email || assignment.recipient_email || '',
          job_position: 'External Stakeholder',
          isExternal: true,
          assignmentId: assignment.id
        }))
      ];
      
      setAssignedStakeholders(combinedAssigned);
      if (onAssignmentChange) onAssignmentChange();
      
      // Show success message
      const stakeholderName = stakeholder.name || `${stakeholder.first_name || ''} ${stakeholder.last_name || ''}`.trim();
      setError(`✅ Successfully removed ${stakeholderName}`);
      setTimeout(() => setError(null), 3000);
      
    } catch (e) {
      console.error('❌ Removal error:', e);
      console.error('Error response:', e.response?.data);
      const stakeholderName = stakeholder.name || `${stakeholder.first_name || ''} ${stakeholder.last_name || ''}`.trim();
      const errorMsg = e.response?.data?.error || `Failed to remove ${stakeholderName}`;
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content assign-stakeholders-modal">
        <div className="modal-header">
          <h2>Assign Stakeholders</h2>
          <button className="close-button" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="modal-body">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or role..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              disabled={loading}
            />
          </div>
          {error && <div className="modal-error">{error}</div>}
          {loading ? (
            <div className="modal-loading">Loading...</div>
          ) : (
            <div className="stakeholder-lists">
              <div className="stakeholder-section">
                <h4>Assigned Stakeholders <span className="count-badge">{assignedStakeholders.length}</span></h4>
                <div className="stakeholder-list">
                  {filteredAssigned.length === 0 ? (
                    <div className="empty-state">No stakeholders assigned.</div>
                  ) : (
                    Object.entries(groupByRole(filteredAssigned, true)).map(([role, group]) => (
                      <div key={role} className="role-group">
                        <div className="role-header"><FaUserTag /> {role}</div>
                        {group.map(emp => {
                          // Handle both internal employees and external stakeholders
                          const name = emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                          const isExternal = emp.isExternal;
                          
                          return (
                            <div key={emp.id} className="stakeholder-item assigned">
                              <span className="stakeholder-name">
                                {name} 
                                <span className="role-badge">{emp.job_position || 'Unspecified'}</span>
                                {isExternal && <span className="type-badge external">External</span>}
                              </span>
                              <div className="stakeholder-actions">
                                <button className="remove-btn" onClick={() => handleRemove(emp)} disabled={saving}><FaUserMinus /></button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="stakeholder-section">
                <h4>Available Project Stakeholders <span className="count-badge">{filteredAvailable.length}</span></h4>
                <div className="note">
                  Showing all stakeholders assigned to this project. <strong>No emails are sent during assignment.</strong> When deployed:
                  <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                    <li>Employees will access via "My Assessments" portal</li>
                    <li>Stakeholders (Internal/External) will receive email invitations</li>
                  </ul>
                  {allStakeholders.length > 0 && (
                    <span> ({allStakeholders.length} total project stakeholders: {allStakeholders.filter(s => s.employee_id).length} internal, {allStakeholders.filter(s => !s.employee_id).length} external)</span>
                  )}
                </div>
                <div className="stakeholder-list">
                  {filteredAvailable.length === 0 ? (
                    <div className="empty-state">
                      {allStakeholders.length === 0 
                        ? "No stakeholders are assigned to this project yet. Assign stakeholders to the project first."
                        : "No additional stakeholders available. All project stakeholders have been assigned or invited."
                      }
                    </div>
                  ) : (
                    Object.entries(groupByRole(filteredAvailable, false)).map(([role, group]) => (
                      <div key={role} className="role-group">
                        <div className="role-header"><FaUserTag /> {role}</div>
                        {group.map(s => {
                          const name = s.display_name || s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim();
                          const isInternal = !!s.employee_id;
                          const actionText = 'Assign'; // Always use "Assign" for all stakeholder types
                          const stakeholderType = isInternal ? 'Internal' : 'External';
                          
                          return (
                            <div key={s.id} className="stakeholder-item available">
                              <span className="stakeholder-name">
                                {name} 
                                <span className="role-badge">{s.job_title || 'Unspecified'}</span>
                                <span className={`type-badge ${isInternal ? 'internal' : 'external'}`}>{stakeholderType}</span>
                              </span>
                              <button 
                                className="add-btn" 
                                onClick={() => handleAssign(s)} 
                                disabled={saving}
                                title={`Assign ${name} to assessment ${isInternal ? '(will access via My Assessments portal)' : '(will receive email when assessment is deployed)'}`}
                              >
                                <FaUserPlus />
                                <span className="btn-text">{actionText}</span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AssignStakeholdersModal; 
